import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import TabNavigation from '../components/TabNavigation';

const UserManagement = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'reporter'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/auth/register`, formData);
      setSuccess(`User ${formData.name} created successfully!`);
      setFormData({ name: '', email: '', password: '', role: 'reporter' });
      setShowAddUser(false);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      admin: 'bg-purple-100 text-purple-700 border-purple-300',
      hse_manager: 'bg-blue-100 text-blue-700 border-blue-300',
      reporter: 'bg-green-100 text-green-700 border-green-300'
    };
    
    const roleNames = {
      admin: 'Administrator',
      hse_manager: 'HSE Manager',
      reporter: 'Reporter'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold border ${roleColors[role] || 'bg-gray-100 text-gray-700'}`}>
        {roleNames[role] || role}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#1B5C94]"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6" data-testid="user-management-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">USER MANAGEMENT</h1>
            <p className="text-gray-600 mt-1">Manage users in your company</p>
          </div>
          <button
            onClick={() => setShowAddUser(!showAddUser)}
            className="mt-4 md:mt-0 bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white font-bold py-3 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
            data-testid="add-user-button"
          >
            {showAddUser ? '✕ Cancel' : '+ Add New User'}
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg" data-testid="success-message">
            ✓ {success}
          </div>
        )}

        {/* Add User Form */}
        {showAddUser && (
          <div className="bg-white rounded-xl shadow-md p-6" data-testid="add-user-form">
            <h2 className="text-xl font-bold text-[#1B5C94] mb-4">Add New User</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="name">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    data-testid="user-name-input"
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="email">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    data-testid="user-email-input"
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                    placeholder="user@company.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="password">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    data-testid="user-password-input"
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                    placeholder="Minimum 6 characters"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="role">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    data-testid="user-role-select"
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] bg-white"
                    required
                  >
                    <option value="reporter">Reporter</option>
                    <option value="hse_manager">HSE Manager</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm" data-testid="error-message">
                  {error}
                </div>
              )}

              <button
                data-testid="submit-user-button"
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white font-bold py-3 px-6 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
              >
                {submitting ? 'Creating User...' : 'Create User'}
              </button>
            </form>
          </div>
        )}

        {/* Users List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB]">
            <h2 className="text-xl font-bold text-white">Company Users ({users.length})</h2>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">#</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((usr, index) => (
                  <tr key={usr.id} className="hover:bg-gray-50" data-testid={`user-row-${usr.id}`}>
                    <td className="px-6 py-4 text-sm text-gray-700">{index + 1}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-800">{usr.name}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{usr.email}</td>
                    <td className="px-6 py-4">{getRoleBadge(usr.role)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${usr.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {usr.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {new Date(usr.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden p-4 space-y-4">
            {users.map((usr, index) => (
              <div key={usr.id} className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-sm font-bold text-gray-500">#{index + 1}</div>
                    <div className="text-lg font-semibold text-gray-800 mt-1">{usr.name}</div>
                    <div className="text-sm text-gray-600 mt-1">{usr.email}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {getRoleBadge(usr.role)}
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${usr.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {usr.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  Created: {new Date(usr.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>

          {users.length === 0 && (
            <div className="p-12 text-center text-gray-500">
              <p className="text-lg font-semibold">No users found</p>
              <p className="text-sm mt-2">Click "Add New User" to create your first user</p>
            </div>
          )}
        </div>
      </div>
    </div>
      </div>
  );
};

export default UserManagement;
